import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import "./SearchDropdown.scss";

import arrowIcon from "assets/icons/arrow-down.svg";
import SearchInput from "components/inputs/SearchInput/SearchInput";
import Loader from "components/global/Loader";

type DropdownEntity = {
  id?: number;
  title?: string;
  name?: string;
};
export interface Item {
  value: number | string;
  label: string;
  icon?: string;
  [key: string]: unknown;
}

export interface PayloadData {
  limit?: number;
  isActive?: 1 | 0;
  providerIds?: number[];
  collectionIds?: number[];
  tagIds?: number[];
  [key: string]: unknown;
}

interface Props {
  label?: string;
  fetchService?: (params: PayloadData) => Promise<DropdownEntity[]>;
  items?: Item[];
  onSelect: (item: Item) => void;
  searchQuery?: string;
  payloadData?: PayloadData;
  defaultValues?: Item[];
  mode?: "single" | "multiple";
  visibleItems?: number;
  isTop?: boolean;
  isBot?: boolean;
  disabled?: boolean;
  hasError?: boolean;
  isClearable?: boolean;
  keepSelectedInList?: boolean;
  onLoadingChange?: (isLoading: boolean) => void;
}

const LIMIT = 50;
const MAX_VISIBLE_DEFAULT = 5;

const SearchDropdown: React.FC<Props> = ({
  label,
  fetchService,
  items,
  onSelect,
  payloadData,
  searchQuery,
  defaultValues,
  mode = "multiple",
  visibleItems = MAX_VISIBLE_DEFAULT,
  isTop = false,
  isBot = false,
  disabled = false,
  hasError = false,
  isClearable = true,
  keepSelectedInList = false,
  onLoadingChange,
}) => {
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(
    !items?.length && Boolean(fetchService),
  );
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [fetchedItems, setFetchedItems] = useState<Item[]>([]);
  const [search, setSearch] = useState("");
  const [selectedItems, setSelectedItems] = useState<Item[]>([]);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const [isMoreOpen, setIsMoreOpen] = useState(false);

  useEffect(() => {
    onLoadingChange?.(isLoading);
  }, [isLoading, onLoadingChange]);

  const isLocalMode = !!items?.length;
  const sourceItems = isLocalMode
    ? items.filter((item) =>
        item.label.toLowerCase().includes(search.toLowerCase()),
      )
    : fetchedItems;
  const mapEntityToItem = (el: DropdownEntity): Item => ({
    value: el.id ?? el.title ?? el.name ?? "",
    label: el.title ?? el.name ?? "",
  });

  useEffect(() => {
    setSelectedItems(defaultValues ?? []);
  }, [defaultValues]);

  const fetchItems = async () => {
    if (!fetchService || isLocalMode) return;
    setIsLoading(true);

    const computedLimit = payloadData?.limit ?? LIMIT;

    const payload: Record<string, any> = {
      limit: computedLimit,
      offset,
      ...payloadData,
      ...(search && searchQuery ? { [searchQuery]: search } : {}),
    };

    try {
      const res = await fetchService(payload);

      const data = Array.isArray(res) ? res.map(mapEntityToItem) : [];

      setFetchedItems((prev) => [...prev, ...data]);
      setHasMore(data.length === computedLimit);
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setOffset(0);
      setFetchedItems([]);
      setHasMore(true);
      setSearch("");
      setIsMoreOpen(false);
      setHighlightedIndex(-1);
    }
  }, [isOpen]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setOffset(0);
      setFetchedItems([]);
      setHasMore(true);
      fetchItems();
    }, 600);
    return () => clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    const handleScroll = () => {
      if (!hasMore || isLoading) return;
      if (!listRef.current) return;

      const { scrollTop, scrollHeight, clientHeight } = listRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 50) {
        setOffset((prev) => prev + 1);
      }
    };

    const el = listRef.current;
    el?.addEventListener("scroll", handleScroll);
    return () => el?.removeEventListener("scroll", handleScroll);
  }, [isOpen, offset, isLoading, hasMore]);

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
        setIsMoreOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const visibleItemsFiltered = keepSelectedInList
    ? sourceItems
    : sourceItems.filter(
        (item) => !selectedItems.some((sel) => sel.value === item.value),
      );

  const visibleSelected = selectedItems.slice(0, visibleItems);
  const hiddenCount = Math.max(0, selectedItems.length - visibleItems);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (disabled || !isOpen || !visibleItemsFiltered.length) return;

      let nextIndex = highlightedIndex;

      if (e.key === "Escape") {
        setIsOpen(false);
        return;
      }

      if (e.key === "ArrowDown") {
        e.preventDefault();
        nextIndex =
          highlightedIndex < visibleItemsFiltered.length - 1
            ? highlightedIndex + 1
            : 0;
      }

      if (e.key === "ArrowUp") {
        e.preventDefault();
        nextIndex =
          highlightedIndex > 0
            ? highlightedIndex - 1
            : visibleItemsFiltered.length - 1;
      }

      if (e.key === "Enter" && highlightedIndex >= 0) {
        e.preventDefault();
        handleSelect(visibleItemsFiltered[highlightedIndex]);
        return;
      }

      if (nextIndex !== highlightedIndex) {
        setHighlightedIndex(nextIndex);

        const node = listRef.current?.children[nextIndex] as
          | HTMLElement
          | undefined;

        if (node) {
          node.scrollIntoView({
            block: "nearest",
            behavior: "auto",
          });
        }
      }
    };

    el.addEventListener("keydown", handleKeyDown);
    return () => el.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, visibleItemsFiltered, highlightedIndex]);

  useEffect(() => {
    if (!isOpen) return;
    fetchItems();
  }, [offset, isOpen]);

  const handleSelect = (item: Item) => {
    if (disabled) return;

    if (mode === "single") {
      setSelectedItems([item]);
      onSelect(item);
      setIsOpen(false);
    }

    if (mode === "multiple") {
      setSelectedItems((prev) => {
        const updated = prev.some((el) => el.value === item.value)
          ? prev.filter((el) => el.value !== item.value)
          : [...prev, item];

        return updated;
      });

      onSelect(item);
      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
    }
  };

  const handleRemove = (value: number | string) => {
    if (disabled) return;

    if (mode == "single") {
      setSelectedItems([]);
      onSelect({ value, label: "", removed: true } as any);
      return;
    }
    if (mode == "multiple") {
      setSelectedItems((prev) => prev.filter((el) => el.value !== value));
      onSelect({ value, label: "", removed: true } as any);
    }
  };

  return (
    <div
      ref={containerRef}
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
      className={`
      search-dropdown
      ${isOpen ? "search-dropdown--opened" : ""}
      ${mode !== "single" ? "search-dropdown--multi" : ""}
      ${isTop && !isBot ? "search-dropdown--open-up" : ""}
      ${disabled ? "search-dropdown--disabled" : ""}
      ${hasError ? "search-dropdown--error" : ""}
    `}
    >
      <div
        className="search-dropdown__select"
        onClick={() => {
          if (!disabled) setIsOpen((v) => !v);
        }}
      >
        <span
          className="search-dropdown__value"
          title={
            mode === "single" && selectedItems.length > 0
              ? selectedItems[0].label
              : undefined
          }
        >
          {mode === "single" && selectedItems[0]?.icon && (
            <img
              className="search-dropdown__flag"
              src={selectedItems[0].icon}
              alt=""
            />
          )}
          {mode === "single" && selectedItems.length > 0
            ? selectedItems[0].label
            : label}
        </span>

        <div className="search-dropdown__actions">
          {mode === "single" && isClearable && selectedItems.length > 0 && (
            <div
              className="search-dropdown__clear"
              onClick={(e) => {
                e.stopPropagation();
                handleRemove(selectedItems[0].value);
              }}
            >
              ✕
            </div>
          )}
          <img className="search-dropdown__arrow" src={arrowIcon} alt="arrow" />
        </div>
      </div>

      <div className="search-dropdown__content">
        <SearchInput
          placeholder={t("search")}
          value={search}
          onChange={(value) => setSearch(value)}
        />

        <div ref={listRef} className="search-dropdown__list">
          {visibleItemsFiltered.map((el, index) => (
            <div
              key={el.value}
              className={`
              search-dropdown__item
              ${
                index === highlightedIndex
                  ? "search-dropdown__item--highlighted"
                  : ""
              }
              ${
                keepSelectedInList &&
                selectedItems.some((item) => item.value === el.value)
                  ? "search-dropdown__item--selected"
                  : ""
              }
            `}
              title={el.label}
              onClick={() => handleSelect(el)}
            >
              {el.icon && (
                <img className="search-dropdown__flag" src={el.icon} alt="" />
              )}
              <span>{el.label}</span>
            </div>
          ))}

          {isLoading && <Loader />}

          {!isLoading && sourceItems.length === 0 && (
            <div className="search-dropdown__empty">{t("nothingFound")}</div>
          )}
        </div>
      </div>

      {mode !== "single" && visibleSelected.length > 0 && (
        <div className="search-dropdown__selected">
          {visibleSelected.map((item) => (
            <div
              key={item.value}
              className="search-dropdown__selected-item"
              onClick={() => handleRemove(item.value)}
            >
              {item.icon && (
                <img className="search-dropdown__flag" src={item.icon} alt="" />
              )}
              <span
                className="search-dropdown__selected-title"
                title={item.label}
              >
                {item.label}
              </span>
              <div className="search-dropdown__selected-remove">✕</div>
            </div>
          ))}

          {hiddenCount > 0 && (
            <div
              className="search-dropdown__more"
              onClick={(e) => {
                e.stopPropagation();
                setIsMoreOpen((v) => !v);
              }}
            >
              {isMoreOpen ? `- ${hiddenCount}` : `+ ${hiddenCount}`}
            </div>
          )}

          {isMoreOpen && hiddenCount > 0 && (
            <div className="search-dropdown__more-popover">
              {selectedItems.slice(visibleItems).map((item) => (
                <div
                  key={item.value}
                  className="search-dropdown__selected-item"
                >
                  {item.icon && (
                    <img
                      className="search-dropdown__flag"
                      src={item.icon}
                      alt=""
                    />
                  )}
                  <span
                    className="search-dropdown__selected-title"
                    title={item.label}
                  >
                    {item.label}
                  </span>
                  <div
                    className="search-dropdown__selected-remove"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemove(item.value);
                    }}
                  >
                    ✕
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchDropdown;
